import {TIMER_TYPE} from "@/types/timer";
import {eGymTrainingsModule} from "../modules/egym-trainings.module";
import {eGymUsersModule} from "../modules/egym-users.module";
import {EGymWorkoutData} from "@/types/e-gym";
import {httpService} from "@/services/http.service";
import {CTRL_COMMAND, CTRL_COMMAND_FEEDBACK, FeedbackCtrlCmdMessage} from "@/types/socket";
import egymTrainingWorkoutActiveModule from "../modules/egym-training-workout-active.module";
import {ctrlCmdModule} from "../modules/control-commands.module";
import {RuntimeState} from "@/types/app";
import router from "@/router";
import {PAGES} from "@/types/router";
import {BaseWorkout} from "@/types/workouts";
import runtimeProcessorModule from "../modules/runtime-processor.module";

class EgymWorkoutActiveManager
{
    async sendData()
    {
        const users = eGymUsersModule.usersCheckedIn.map(user => user.rfid);
        const exercises = eGymTrainingsModule.timeframesTrace
            .map(trace =>
            {
                trace.sets[0].duration *= 1000;
                return trace;
            })
        
        const report = {
            exercises,
            users
        } as EGymWorkoutData;
        httpService.egymReportSend(report).catch(() => eGymTrainingsModule.setBacklog(report))
            .finally(() =>
            {
                eGymTrainingsModule.resetToDefault();
                eGymUsersModule.resetToDefault();
            });
    }

    goToCelebrationPage()
    {
        let totalTime = egymTrainingWorkoutActiveModule.activeWorkoutTimeFramesArray
        .reduce((time, tf) => {
            if (tf.workInterval != true) time += tf.duration
            return time
        }, 0);
        totalTime = eGymTrainingsModule.timeframesTrace.reduce((time, timeFrame) => {
            return time += timeFrame.sets[0].duration
        }, totalTime)
        router.push({ name: PAGES.CELEBRATION, params: { totalTime: totalTime.toString() } });
    }

    timeFrameStart()
    {
        const {duration} = egymTrainingWorkoutActiveModule.activeTimeFrame;
        if (duration == -1) {
            runtimeProcessorModule.changeTimerType(TIMER_TYPE.UP);
            runtimeProcessorModule.totalTimeReset(0);
            runtimeProcessorModule.start(0);
        } else if (duration > 0) {
            runtimeProcessorModule.totalTimeReset(duration);
            runtimeProcessorModule.changeTimerType(TIMER_TYPE.DOWN);
            runtimeProcessorModule.start(duration);
        } else if (!egymTrainingWorkoutActiveModule.isItTheLastTimeFrame) {
            egymTrainingWorkoutActiveModule.loadTheNextTimeFrame();
            this.timeFrameStart();
        }
    }

    start()
    {
        const {id, totalTime} = egymTrainingWorkoutActiveModule.workout as BaseWorkout;
        ctrlCmdModule.triggerCtrlCmd({trigger: CTRL_COMMAND.MSS_START, value: {id, time: totalTime}});
    }

    stop()
    {
        const activeTimeFrame = egymTrainingWorkoutActiveModule.activeTimeFrame
        const isItTheLastTimeFrame = egymTrainingWorkoutActiveModule.isItTheLastTimeFrame
        if (isItTheLastTimeFrame && activeTimeFrame != undefined && activeTimeFrame.duration == -1) {
            const duration = runtimeProcessorModule.currentTime / 1000
            eGymTrainingsModule.traceTimeFrame({ ...activeTimeFrame, duration });
        }
        if (runtimeProcessorModule.isItInProcess) {
            runtimeProcessorModule.stop();
        }
        const {id} = egymTrainingWorkoutActiveModule.workout as BaseWorkout;
        ctrlCmdModule.triggerCtrlCmd({trigger: CTRL_COMMAND.WORKOUT_STOP, value: { id }});
    }

    forward()
    {
        const {paused} = runtimeProcessorModule.status;
        if (paused) runtimeProcessorModule.resume();
        const {id} = egymTrainingWorkoutActiveModule.workout as BaseWorkout;
        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_FORWARD, value: {id}
        });
    }

    backward()
    {
        const {paused} = runtimeProcessorModule.status;
        if (paused) runtimeProcessorModule.resume();
        const {id} = egymTrainingWorkoutActiveModule.workout as BaseWorkout;
        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_BACKWARD, value: {id}
        });
    }

    feedbackCmdWatcher(cmd?: FeedbackCtrlCmdMessage)
    {
        if (cmd != undefined) {
            const activeTimeFrame = egymTrainingWorkoutActiveModule.activeTimeFrame;
            if (cmd.event == CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_FORWARD && activeTimeFrame != undefined) {
                if (activeTimeFrame.duration == -1) {
                    const duration = runtimeProcessorModule.currentTime / 1000
                    eGymTrainingsModule.traceTimeFrame({ ...activeTimeFrame, duration });
                } else if (activeTimeFrame.duration > 0 && cmd.value.timeFrameIdx != egymTrainingWorkoutActiveModule.activeWorkoutTimeFramesArrayIdx) {
                    const duration = (runtimeProcessorModule.totalTime - runtimeProcessorModule.currentTime) / 1000
                    eGymTrainingsModule.traceTimeFrame({ ...activeTimeFrame, duration });
                }
            }
            // synchronize time frame
            if ([
                CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_BACKWARD,
                CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_FORWARD,
                CTRL_COMMAND_FEEDBACK.WORKOUT_PAUSED
            ].includes(cmd.event)) {
                egymTrainingWorkoutActiveModule.sync(cmd);
            }
            // automaticaly start a time frame
            if ([
                CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_BACKWARD,
                CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_FORWARD,
                CTRL_COMMAND_FEEDBACK.WORKOUT_STARTED
            ].includes(cmd.event)) {
                this.timeFrameStart();
            }
            ctrlCmdModule.cmdDisposal(cmd.event);
        }
    }

    runtimeStateWatcher(state: RuntimeState)
    {
        const {stopped, finished} = state;
        if (stopped) {
            const activeTimeFrame = egymTrainingWorkoutActiveModule.activeTimeFrame
            const isItTheLastTimeFrame = egymTrainingWorkoutActiveModule.isItTheLastTimeFrame
            if (isItTheLastTimeFrame && activeTimeFrame != undefined && activeTimeFrame.duration == -1) {
                this.goToCelebrationPage()
            } else {
                router.back();
            }
        } else if (finished) { // finished automatically
            const activeTimeFrame = egymTrainingWorkoutActiveModule.activeTimeFrame;
            if (activeTimeFrame.duration > 0) {
                eGymTrainingsModule.traceTimeFrame(activeTimeFrame);
            }
            if (!egymTrainingWorkoutActiveModule.isItTheLastTimeFrame) {
                egymTrainingWorkoutActiveModule.loadTheNextTimeFrame();
                this.timeFrameStart();
            } else {
                this.goToCelebrationPage()
            }
        }
    }
}

export default new EgymWorkoutActiveManager;